import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, TextareaAutosize } from "@mui/material";

import Title from "../../../common/Title";

import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import { __purchasePlanPWG, __cancelPurchasePWG } from "../../../../api/sim";
import { PLAN_PURCHASE_OPERATIONS } from "../../../../utils/planPurchase";
import PwgPlanSelect from "../PwgPlanSelect";

const MDN_REGEX = /^\d{10}$/;
const ZIP_REGEX = /^\d{5}$/;
const PURCHASE_ID_REGEX = /^\d{1,35}$/;

// Returns { line } on success or { error } describing what is wrong.
const parsePurchaseLine = (parts) => {
  const [mdn, street1, street2, city, state, zip] = parts;
  if (!MDN_REGEX.test(mdn || "")) return { error: "MDN must be 10 digits" };
  if (zip && !ZIP_REGEX.test(zip)) return { error: "ZIP must be 5 digits" };
  return {
    line: {
      mdn,
      street1: street1 || null,
      street2: street2 || null,
      city: city || null,
      state: state || null,
      e911_zip: zip || null,
    },
  };
};

const parseCancelLine = (parts) => {
  const [mdn, purchaseId] = parts;
  if (!MDN_REGEX.test(mdn || "")) return { error: "MDN must be 10 digits" };
  if (purchaseId && !PURCHASE_ID_REGEX.test(purchaseId)) {
    return { error: "Purchase ID must be numeric (max 35 digits)" };
  }
  return { line: { mdn, purchase_id: purchaseId || null } };
};

const PlanPurchaseForm = ({ operation }) => {
  const config = PLAN_PURCHASE_OPERATIONS[operation];
  const parseLine =
    operation === "purchase_plan" ? parsePurchaseLine : parseCancelLine;

  const [planId, setPlanId] = useState("");
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [linesError, setLinesError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const { setAlertMessage } = useAlert();
  const [response, call, loading] = useAxios({
    ...(operation === "purchase_plan" ? __purchasePlanPWG : __cancelPurchasePWG),
  });

  const nav = useNavigate();

  useEffect(() => {
    if (submitted) {
      call({
        data: {
          lines:
            operation === "purchase_plan"
              ? linesData.map((line) => ({ ...line, plan_id: planId }))
              : linesData,
        },
      });
      setSubmitted(false);
    }
  }, [submitted]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setAlertMessage({ type: "success", message: config.queueMessage });
      setLinesData([]);
      nav(config.route);
    }
  }, [response]);

  const validateFunction = (data) => {
    const formatedLines = [];
    let error = "";

    const lines = (data || "").split("\n").filter((line) => line.trim());
    lines.some((line, idx) => {
      const parts = line.split(",").map((part) => part.trim());
      const { line: parsed, error: lineError } = parseLine(parts);
      if (lineError) {
        error = `Invalid format in line ${idx + 1}: ${line} (${lineError})`;
        return true;
      }
      formatedLines.push(parsed);
      return false;
    });

    setLinesError(error);
    setLinesData(error ? [] : formatedLines);
    return error;
  };

  const handleParse = (inputFile) => {
    const reader = new FileReader();

    reader.onload = async ({ target }) => {
      setRawData(target.result);
      validateFunction(target.result);
    };
    reader.readAsText(inputFile);
  };

  const handleFileChange = (e) => {
    const allowedExtensions = ["csv"];

    if (e.target.files.length) {
      const inputFile = e.target.files[0];

      const fileExtension = inputFile?.type.split("/")[1];
      if (!allowedExtensions.includes(fileExtension)) {
        setUploadError("Please input a csv file");
        return;
      }

      setUploadError("");
      handleParse(inputFile);
    }
  };

  if (loading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <Box
      sx={{ width: "100%" }}
      display="flex"
      alignItems="center"
      flexDirection="column"
    >
      <Title
        variant="h5"
        content={`${config.title} (PWG)`}
        sx={{ mb: 3 }}
        gutterBottom
      />
      <Grid
        sx={{ flexGrow: 1 }}
        container
        spacing={2}
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        {operation === "purchase_plan" && (
          <Grid item xs={12} minWidth={350}>
            <PwgPlanSelect label="Plan" value={planId} onChange={setPlanId} />
          </Grid>
        )}
        <Grid item xs={12}>
          <FormControl error={Boolean(uploadError)} sx={{ width: "100%" }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              sx={{ marginRight: "1rem" }}
            >
              Upload CSV
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            {Boolean(uploadError) ? (
              <FormHelperText id="upload-error-text">
                {uploadError}
              </FormHelperText>
            ) : (
              ""
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <InputLabel htmlFor="lines">
            {"Add in the format"}
            <br></br>
            {config.format}
            <br></br>
          </InputLabel>
          <FormControl error={Boolean(linesError)} sx={{ width: "100%" }}>
            <TextareaAutosize
              multiline
              rows={6}
              aria-label="minimum height"
              placeholder={`Add in the format\n${config.format}`}
              style={{ minWidth: 550 }}
              minRows={5}
              name="lines"
              id="lines"
              value={rawData}
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const data = e.target.value;
                setRawData(data);
                validateFunction(data);
              }}
            />
            {Boolean(linesError) ? (
              <FormHelperText id="lines-error-text">{linesError}</FormHelperText>
            ) : (
              ""
            )}
          </FormControl>
        </Grid>

        <Grid
          container
          sx={{ mt: 3 }}
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item>
            <LoadingButton
              style={{ marginRight: "5px" }}
              disabled={
                Boolean(linesError) ||
                !linesData.length ||
                (operation === "purchase_plan" && !planId)
              }
              onClick={() => !loading && setSubmitted(true)}
              size="large"
              loading={loading}
              variant="contained"
            >
              {config.submitLabel}
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlanPurchaseForm;
